import { Globe, Lightbulb, MessageSquare, Palette, Users } from "lucide-react";
import { resolveBrandLogoCarrierFill, type OnboardingStage } from "@runcheetah/shared";

type BrandStatus = "queued" | "processing" | "scanned" | "failed";

export const ANALYZE_STEPS = [
  {
    key: "positioning",
    icon: Globe,
    label: "Understanding your positioning",
    helper: "Reading your story, value, and what makes you different",
  },
  {
    key: "visual_identity",
    icon: Palette,
    label: "Picking up your visual world",
    helper: "Colors, marks, and the mood you carry",
  },
  {
    key: "voice",
    icon: MessageSquare,
    label: "Learning how your brand speaks",
    helper: "Voice, rhythm, the way you choose words",
  },
  {
    key: "audience",
    icon: Users,
    label: "Finding the people behind the clicks",
    helper: "Who you write for, and what they're really here for",
  },
  {
    key: "content_angles",
    icon: Lightbulb,
    label: "Discovering what your audience cares about",
    helper: "The stories likely to land with your audience",
  },
] as const;

const FALLBACK_DESCRIPTION = "No details available yet.";

export type AnalyzeStepKey = (typeof ANALYZE_STEPS)[number]["key"];
export type AnalyzeStepStatus = "queued" | "processing" | "partial" | "done" | "failed";
export type AnalyzeStepSummary = {
  key: AnalyzeStepKey;
  status: AnalyzeStepStatus;
};

const ANALYZE_TERMINAL_STATUSES = new Set<AnalyzeStepStatus>(["done", "partial", "failed"]);
const ANALYZE_STEP_STATUS_RANK: Record<AnalyzeStepStatus, number> = {
  queued: 0,
  processing: 1,
  partial: 2,
  failed: 3,
  done: 4,
};

export const isTerminalAnalyzeStepStatus = (status: AnalyzeStepStatus): boolean =>
  ANALYZE_TERMINAL_STATUSES.has(status);

export const mergeAnalyzeStepSummaries = (
  previous: AnalyzeStepSummary[] | null | undefined,
  incoming: AnalyzeStepSummary[] | null | undefined,
): AnalyzeStepSummary[] => {
  if (!previous?.length) return incoming ?? [];
  if (!incoming?.length) return previous;

  const byKey = new Map(previous.map((step) => [step.key, step] as const));
  for (const step of incoming) {
    const current = byKey.get(step.key);
    if (isTerminalAnalyzeStepStatus(step.status)) {
      byKey.set(step.key, step);
      continue;
    }

    if (
      !current ||
      (!isTerminalAnalyzeStepStatus(current.status) &&
        ANALYZE_STEP_STATUS_RANK[step.status] >= ANALYZE_STEP_STATUS_RANK[current.status])
    ) {
      byKey.set(step.key, step);
    }
  }

  return incoming.map((step) => byKey.get(step.key) ?? step);
};

export const getInitialAnalyzeStepDisplayStatuses = (
  stepStatuses: Record<AnalyzeStepKey, AnalyzeStepStatus>,
): Record<AnalyzeStepKey, AnalyzeStepStatus> =>
  Object.fromEntries(
    ANALYZE_STEPS.map((step) => [
      step.key,
      isTerminalAnalyzeStepStatus(stepStatuses[step.key]) ? "processing" : stepStatuses[step.key],
    ]),
  ) as Record<AnalyzeStepKey, AnalyzeStepStatus>;

export const getNextAnalyzeStepDisplayStatuses = (input: {
  previous: Record<AnalyzeStepKey, AnalyzeStepStatus>;
  target: Record<AnalyzeStepKey, AnalyzeStepStatus>;
}): {
  stepStatuses: Record<AnalyzeStepKey, AnalyzeStepStatus>;
  stagedKeys: AnalyzeStepKey[];
} => {
  const stagedKeys: AnalyzeStepKey[] = [];
  const stepStatuses = Object.fromEntries(
    ANALYZE_STEPS.map((step) => {
      const targetStatus = input.target[step.key];
      const previousStatus = input.previous[step.key];

      if (isTerminalAnalyzeStepStatus(targetStatus) && previousStatus !== targetStatus) {
        stagedKeys.push(step.key);
        return [step.key, "processing"];
      }

      return [step.key, targetStatus];
    }),
  ) as Record<AnalyzeStepKey, AnalyzeStepStatus>;

  return { stepStatuses, stagedKeys };
};

export const shouldShowAnalyzeWrappingStep = (input: {
  status: BrandStatus;
  stepStatuses: Record<AnalyzeStepKey, AnalyzeStepStatus>;
}): boolean =>
  input.status !== "failed" &&
  ANALYZE_STEPS.every((step) =>
    ["done", "partial", "failed"].includes(input.stepStatuses[step.key]),
  );

const formatCompactDurationSeconds = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }

  return `${seconds}s`;
};

export const formatAnalyzeExpectationDuration = (
  estimatedReadyMinutes: number | null | undefined,
): string | null => {
  if (
    typeof estimatedReadyMinutes !== "number" ||
    !Number.isFinite(estimatedReadyMinutes) ||
    estimatedReadyMinutes <= 0
  ) {
    return null;
  }

  const totalSeconds = Math.max(1, Math.round(estimatedReadyMinutes * 60));
  return formatCompactDurationSeconds(totalSeconds);
};

const trim = (value: string | null | undefined): string | null => {
  const next = value?.trim();
  return next ? next : null;
};

const uniqueValues = (values: Array<string | null | undefined>): string[] => {
  const seen = new Set<string>();

  return values.reduce<string[]>((acc, value) => {
    const next = trim(value);
    if (!next) {
      return acc;
    }

    const key = next.toLowerCase();
    if (seen.has(key)) {
      return acc;
    }

    seen.add(key);
    acc.push(next);
    return acc;
  }, []);
};

export const getStageLabel = (stage: OnboardingStage | null, status: BrandStatus): string => {
  switch (stage) {
    case "queued":
      return "Queued";
    case "processing_origin":
      return "Scanning origin page";
    case "processing_pages":
      return "Processing discovered pages";
    case "extracting_facts":
      return "Extracting brand facts";
    case "extracting_visual_identity":
      return "Analyzing visual identity";
    default:
      return status === "processing" ? "Processing" : "Preparing brand profile";
  }
};

export const getAnalyzeProgress = (input: {
  stage: OnboardingStage | null;
  status: BrandStatus;
  profile: OnboardingProfileLike | null;
  voice: OnboardingVoiceLike | null;
  visual: OnboardingVisualLike | null;
  analysisSteps?: AnalyzeStepSummary[] | null;
}): {
  activeIndex: number;
  completedIndexes: number[];
  stepStatuses: Record<AnalyzeStepKey, AnalyzeStepStatus>;
} => {
  // UI steps stay in a stable display order, while each row reports its own backend status:
  //   0 "Understanding your positioning"        → fact job/profile facts
  //   1 "Picking up your visual world"          → visual identity job/visual facts
  //   2 "Learning how your brand speaks"        → fact job/voice facts
  //   3 "Finding the people behind clicks"      → fact job/audience facts
  //   4 "Discovering what your audience cares"  → fact job/content-angle facts
  // Post generation starts after visual identity and is shown by the separate generating step.
  const hasPositioning = hasPositioningData(input.profile);
  const hasVoice = hasVoiceData(input.voice);
  const hasAudience = hasAudienceData(input.profile);
  const hasVisual = hasVisualData(input.visual);
  const hasCompleteVisualIdentity = hasCompleteVisualIdentityData(input.visual);
  const hasContentAngles = (input.voice?.contentAngles ?? []).length > 0;
  const isFinalized = input.status === "scanned" || input.stage === "ready";

  const fallbackStatuses: Record<AnalyzeStepKey, AnalyzeStepStatus> = {
    positioning: "queued",
    visual_identity: "queued",
    voice: "queued",
    audience: "queued",
    content_angles: "queued",
  };

  for (const step of input.analysisSteps ?? []) {
    fallbackStatuses[step.key] = step.status;
  }

  const markDone = (key: AnalyzeStepKey) => {
    if (fallbackStatuses[key] !== "failed" && fallbackStatuses[key] !== "partial") {
      fallbackStatuses[key] = "done";
    }
  };

  const markProcessing = (key: AnalyzeStepKey) => {
    if (
      fallbackStatuses[key] !== "done" &&
      fallbackStatuses[key] !== "partial" &&
      fallbackStatuses[key] !== "failed"
    ) {
      fallbackStatuses[key] = "processing";
    }
  };

  const markFailed = (key: AnalyzeStepKey) => {
    fallbackStatuses[key] = "failed";
  };

  const getCompletedIndexes = () =>
    ANALYZE_STEPS.flatMap((step, index) => (fallbackStatuses[step.key] === "done" ? [index] : []));

  const getActiveIndex = (fallbackIndex: number) => {
    const processingIndex = ANALYZE_STEPS.findIndex(
      (step) => fallbackStatuses[step.key] === "processing",
    );
    if (processingIndex >= 0) return processingIndex;

    const queuedIndex = ANALYZE_STEPS.findIndex((step) => fallbackStatuses[step.key] === "queued");
    return queuedIndex >= 0 ? queuedIndex : fallbackIndex;
  };

  const finalizeIncompleteStep = (key: AnalyzeStepKey, hasData: boolean) => {
    if (hasData && fallbackStatuses[key] !== "partial") {
      markDone(key);
      return;
    }

    markFailed(key);
  };

  const keepOnlyCurrentProcessingStep = () => {
    const currentProcessingKey = ANALYZE_STEPS.find(
      (step) => fallbackStatuses[step.key] === "processing",
    )?.key;

    if (!currentProcessingKey) {
      return;
    }

    for (const step of ANALYZE_STEPS) {
      if (step.key !== currentProcessingKey && fallbackStatuses[step.key] === "processing") {
        fallbackStatuses[step.key] = "queued";
      }
    }
  };

  const markQueuedStepsProcessing = () => {
    for (const step of ANALYZE_STEPS) {
      if (fallbackStatuses[step.key] === "queued") {
        fallbackStatuses[step.key] = "processing";
      }
    }
  };

  if (hasCompleteVisualIdentity) {
    fallbackStatuses.visual_identity = "done";
  } else if (hasVisual) {
    markDone("visual_identity");
  }
  if (hasVoice) markDone("voice");
  if (hasAudience) markDone("audience");
  if (hasContentAngles) markDone("content_angles");
  if (hasPositioning) {
    markDone("positioning");
  }

  if (isFinalized) {
    finalizeIncompleteStep("positioning", hasPositioning);
    finalizeIncompleteStep("visual_identity", hasCompleteVisualIdentity);
    finalizeIncompleteStep("voice", hasVoice);
    finalizeIncompleteStep("audience", hasAudience);
    finalizeIncompleteStep("content_angles", hasContentAngles);

    return {
      activeIndex: getActiveIndex(4),
      completedIndexes: getCompletedIndexes(),
      stepStatuses: fallbackStatuses,
    };
  }

  if (input.status === "scanned") {
    return {
      activeIndex: 4,
      completedIndexes: getCompletedIndexes(),
      stepStatuses: fallbackStatuses,
    };
  }

  const hasExplicitAnalysisSteps = Boolean(input.analysisSteps && input.analysisSteps.length > 0);
  const hasNoStartedAnalysisSteps =
    hasExplicitAnalysisSteps &&
    ANALYZE_STEPS.every((step) => fallbackStatuses[step.key] === "queued");

  if (hasNoStartedAnalysisSteps && input.status === "processing") {
    markProcessing("positioning");
  }

  if (hasExplicitAnalysisSteps) {
    if (
      input.status === "processing" &&
      (input.stage === "processing_pages" ||
        input.stage === "extracting_facts" ||
        input.stage === "extracting_visual_identity" ||
        input.stage === "ready")
    ) {
      markQueuedStepsProcessing();
    }

    if (
      input.status === "processing" &&
      ANALYZE_STEPS.every((step) => fallbackStatuses[step.key] !== "processing")
    ) {
      const nextStep = ANALYZE_STEPS.find((step) => fallbackStatuses[step.key] === "queued");
      if (nextStep) {
        markProcessing(nextStep.key);
      }
    }
    return {
      activeIndex: getActiveIndex(Math.max(0, ANALYZE_STEPS.length - 1)),
      completedIndexes: getCompletedIndexes(),
      stepStatuses: fallbackStatuses,
    };
  }

  // Each step only advances when its data has actually arrived.
  // The backend stage sets the floor when detailed worker steps are unavailable.
  switch (input.stage) {
    case "queued":
    case "processing_origin":
      markProcessing("positioning");
      keepOnlyCurrentProcessingStep();
      return {
        activeIndex: getActiveIndex(0),
        completedIndexes: getCompletedIndexes(),
        stepStatuses: fallbackStatuses,
      };

    case "processing_pages":
      markDone("positioning");
      if (!hasCompleteVisualIdentity) markProcessing("visual_identity");
      markQueuedStepsProcessing();
      return {
        activeIndex: getActiveIndex(hasCompleteVisualIdentity ? 2 : 1),
        completedIndexes: getCompletedIndexes(),
        stepStatuses: fallbackStatuses,
      };

    case "extracting_facts":
      markProcessing("positioning");
      if (hasVoice) markDone("voice");
      else markProcessing("voice");
      if (hasAudience) markDone("audience");
      else markProcessing("audience");
      if (hasContentAngles) markDone("content_angles");
      else markProcessing("content_angles");
      markQueuedStepsProcessing();
      return {
        activeIndex: getActiveIndex(hasVoice ? (hasAudience ? 4 : 3) : 2),
        completedIndexes: getCompletedIndexes(),
        stepStatuses: fallbackStatuses,
      };

    case "extracting_visual_identity":
      if (hasCompleteVisualIdentity) {
        fallbackStatuses.visual_identity = "done";
      } else {
        markProcessing("visual_identity");
      }
      markQueuedStepsProcessing();
      return {
        activeIndex: getActiveIndex(hasCompleteVisualIdentity ? 2 : 1),
        completedIndexes: getCompletedIndexes(),
        stepStatuses: fallbackStatuses,
      };

    case "ready":
      for (const step of ANALYZE_STEPS) {
        if (fallbackStatuses[step.key] === "queued") {
          markProcessing(step.key);
          break;
        }
      }
      keepOnlyCurrentProcessingStep();
      return {
        activeIndex: 4,
        completedIndexes: getCompletedIndexes(),
        stepStatuses: fallbackStatuses,
      };

    default:
      if (input.status === "processing") {
        keepOnlyCurrentProcessingStep();
        return {
          activeIndex: getActiveIndex(4),
          completedIndexes: getCompletedIndexes(),
          stepStatuses: fallbackStatuses,
        };
      }

      return {
        activeIndex: 0,
        completedIndexes: getCompletedIndexes(),
        stepStatuses: fallbackStatuses,
      };
  }
};

export type OnboardingProfileLike = {
  brandName: string | null;
  tagline: string | null;
  mission: string | null;
  uniqueSellingProposition: string | null;
  marketCategory: string | null;
  primaryAudienceSegments: string[] | null;
  secondaryAudienceSegments: string[] | null;
  psychographics: string | null;
  painPoints: string[] | null;
  desiresAspirations: string[] | null;
  buyingTriggers: string[] | null;
  perceivedPositioning: string[] | null;
  keyDifferentiators: string[] | null;
  competitiveAdvantages: string[] | null;
};

export type OnboardingVoiceLike = {
  tone: string | null;
  voice: string | null;
  vocabularyStyle: string | null;
  sentenceComplexity: string | null;
  useOfEmoji?: string | null;
  culturalReferences: string | null;
  restrictedTopics: string[] | null;
  wordsToAvoid: string[] | null;
  wordsToEmphasize?: string[] | null;
  contentDnaPrimaryStyle?: string | null;
  contentDnaSecondaryStyle?: string | null;
  contentDnaConfidence?: string | null;
  contentDnaHumorLevel?: string | null;
  contentDnaOpinionatedness?: string | null;
  contentDnaAspirationLevel?: string | null;
  contentDnaEducationLevel?: string | null;
  contentDnaCommunityLevel?: string | null;
  socialMechanics?: string[] | null;
  socialAvoidedMechanics?: string[] | null;
  socialTruths?: string[] | null;
  audienceTensions?: string[] | null;
  shareabilityTriggers?: string[] | null;
  contentAngles?: string[] | null;
  contentAntiPatterns?: string[] | null;
  contentDnaRationale?: string | null;
};

export type OnboardingVisualLike = {
  colorPalette: string[] | null;
  secondaryColorPalette?: string[] | null;
  visualMood: string | null;
  imageStyle: string | null;
  logoStyle: string | null;
  designConsistencyLevel: string | null;
  logoUrl?: string | null;
};

export const hasPositioningData = (profile: OnboardingProfileLike | null | undefined): boolean =>
  Boolean(
    profile &&
    uniqueValues([
      profile.brandName,
      profile.tagline,
      profile.mission,
      profile.uniqueSellingProposition,
      profile.marketCategory,
      ...(profile.perceivedPositioning ?? []),
      ...(profile.keyDifferentiators ?? []),
    ]).length > 0,
  );

export const hasVoiceData = (voice: OnboardingVoiceLike | null | undefined): boolean =>
  Boolean(voice && deriveVoiceBasics(voice).length > 0);

export const hasAudienceData = (profile: OnboardingProfileLike | null | undefined): boolean =>
  Boolean(profile && deriveAudienceCards(profile).length > 0);

export const hasVisualData = (visual: OnboardingVisualLike | null | undefined): boolean =>
  Boolean(
    visual &&
    ((visual.colorPalette ?? []).filter(Boolean).length > 0 || trim(visual.logoUrl) !== null),
  );

const hasCompleteVisualIdentityData = (visual: OnboardingVisualLike | null | undefined): boolean =>
  Boolean(
    visual &&
    trim(visual.logoUrl) !== null &&
    (visual.colorPalette ?? []).filter(Boolean).length > 0,
  );

export const getLogoPreviewCarrierFill = (
  visual: Pick<OnboardingVisualLike, "colorPalette" | "secondaryColorPalette"> | null | undefined,
): string =>
  resolveBrandLogoCarrierFill([
    ...(visual?.colorPalette ?? []),
    ...(visual?.secondaryColorPalette ?? []),
  ]);

export const deriveBrandSummary = (input: {
  sourceDomain: string;
  profile: OnboardingProfileLike;
}): string => {
  return (
    uniqueValues(input.profile.keyDifferentiators ?? [])[0] ??
    trim(input.profile.uniqueSellingProposition) ??
    trim(input.profile.mission) ??
    trim(input.profile.tagline) ??
    (trim(input.profile.marketCategory)
      ? `${input.sourceDomain} is positioned in ${input.profile.marketCategory}.`
      : `Cheetah mapped ${input.sourceDomain}'s brand, positioning, and audience from its live source material.`)
  );
};

export const deriveAudienceCards = (
  profile: OnboardingProfileLike,
): Array<{
  title: string;
  description: string;
}> => {
  const segments = uniqueValues([
    ...(profile.primaryAudienceSegments ?? []),
    ...(profile.secondaryAudienceSegments ?? []),
  ]);
  const painPoints = uniqueValues(profile.painPoints ?? []);
  const desires = uniqueValues(profile.desiresAspirations ?? []);
  const triggers = uniqueValues(profile.buyingTriggers ?? []);
  const positioningSignals = uniqueValues([
    profile.uniqueSellingProposition,
    ...(profile.perceivedPositioning ?? []),
    ...(profile.keyDifferentiators ?? []),
    ...(profile.competitiveAdvantages ?? []),
  ]);

  if (segments.length > 0) {
    return segments.map((segment, index) => ({
      title: segment,
      description: painPoints[index] ?? "",
    }));
  }

  const inferredCards = [
    painPoints[0]
      ? {
          title: "Pain points",
          description: painPoints[0],
        }
      : null,
    desires[0]
      ? {
          title: "Aspirations",
          description: desires[0],
        }
      : null,
    triggers[0]
      ? {
          title: "Buying triggers",
          description: triggers[0],
        }
      : null,
    trim(profile.psychographics)
      ? {
          title: "Audience mindset",
          description: trim(profile.psychographics)!,
        }
      : null,
    trim(profile.marketCategory) || positioningSignals[0]
      ? {
          title: trim(profile.marketCategory)
            ? `${trim(profile.marketCategory)} buyers`
            : "Likely buyers",
          description:
            positioningSignals[0] ??
            trim(profile.uniqueSellingProposition) ??
            "People evaluating whether this brand fits their needs.",
        }
      : null,
  ].filter((card): card is { title: string; description: string } => card !== null);

  if (inferredCards.length === 0) {
    return [];
  }

  return inferredCards.slice(0, 3);
};

export const deriveEditableAudienceCards = (
  profile: OnboardingProfileLike,
): Array<{
  title: string;
  description: string;
}> => {
  if (profile.primaryAudienceSegments !== null) {
    return uniqueValues(profile.primaryAudienceSegments).map((segment) => ({
      title: segment,
      description: "",
    }));
  }

  if (profile.secondaryAudienceSegments !== null) {
    return uniqueValues(profile.secondaryAudienceSegments).map((segment) => ({
      title: segment,
      description: "",
    }));
  }

  return deriveAudienceCards(profile);
};

export const deriveVoiceBasics = (
  voice: OnboardingVoiceLike,
): Array<{ key: string; label: string; value: string }> =>
  [
    { key: "tone", label: "Tone", value: trim(voice.tone) },
    { key: "voice", label: "Voice", value: trim(voice.voice) },
    { key: "vocabularyStyle", label: "Vocabulary", value: trim(voice.vocabularyStyle) },
    {
      key: "sentenceComplexity",
      label: "Sentence complexity",
      value: trim(voice.sentenceComplexity),
    },
    { key: "useOfEmoji", label: "Emoji usage", value: trim(voice.useOfEmoji) },
    {
      key: "culturalReferences",
      label: "Cultural references",
      value: trim(voice.culturalReferences),
    },
  ].flatMap((item) => (item.value ? [{ ...item, value: item.value }] : []));

export const deriveVoiceSummary = (voice: OnboardingVoiceLike): string =>
  deriveVoiceBasics(voice)
    .map(({ label, value }) => `${label}: ${value}`)
    .join(" · ") || FALLBACK_DESCRIPTION;

export const deriveEditableContentDnaAngles = (voice: OnboardingVoiceLike): string[] =>
  uniqueValues(voice.contentAngles ?? []).slice(0, 6);

export const deriveGuardrails = (voice: OnboardingVoiceLike): string[] =>
  uniqueValues([...(voice.wordsToAvoid ?? []), ...(voice.restrictedTopics ?? [])]).slice(0, 6);

export const deriveVisualChips = (visual: OnboardingVisualLike): string[] =>
  uniqueValues([
    visual.visualMood,
    visual.imageStyle,
    visual.logoStyle,
    visual.designConsistencyLevel,
  ]).slice(0, 4);
